import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Eye, EyeOff, Loader2, Mail, Lock, ArrowLeft, Settings, KeyRound } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { useUser } from "../context/UserContext";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { loginWithToken } = useUser();
  
  const [step, setStep] = useState("email"); // email | password | setup
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  
  // Admin access modal state
  const [showAdminModal, setShowAdminModal] = useState(false);
  const [adminEmail, setAdminEmail] = useState("");
  const [adminPassword, setAdminPassword] = useState("");
  const [adminPin, setAdminPin] = useState("");
  const [adminStep, setAdminStep] = useState("credentials"); // credentials | pin
  const [adminLoading, setAdminLoading] = useState(false);
  
  // Get redirect path from location state (supports both formats)
  const from = location.state?.returnTo || location.state?.from?.pathname || "/dashboard";
  
  // Pre-fill email if coming from EmailCapture redirect
  useEffect(() => {
    if (location.state?.email) {
      setEmail(location.state.email);
      // Auto-check the email to move to next step
      handleAutoCheckEmail(location.state.email);
    }
  }, [location.state?.email]);
  
  const handleAutoCheckEmail = async (emailToCheck) => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/check-email`, { email: emailToCheck.toLowerCase().trim() });
      const { status } = response.data;
      
      if (status === "needs_password") {
        setStep("setup");
        toast.info("¡Bienvenido de vuelta! Crea tu contraseña para continuar.");
      } else if (status === "has_password") {
        setStep("password");
      }
    } catch (error) {
      console.error("Auto check email error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCheckEmail = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error("Por favor ingresa tu email");
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/check-email`, { email: email.toLowerCase().trim() });
      const { status } = response.data;
      
      if (status === "new_user") {
        // Redirect to register
        navigate("/register", { state: { email: email.toLowerCase().trim(), from } });
      } else if (status === "needs_password") {
        // User exists but no password - show setup password form
        setStep("setup");
        toast.info("¡Bienvenido de vuelta! Crea tu contraseña para continuar.");
      } else {
        // User has password - show login form
        setStep("password");
      }
    } catch (error) {
      console.error("Error checking email:", error);
      toast.error("Error al verificar el email");
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!password) {
      toast.error("Por favor ingresa tu contraseña");
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login`, {
        email: email.toLowerCase().trim(),
        password
      });
      
      if (response.data.status === "needs_password") {
        setStep("setup");
        toast.info("Necesitas crear una contraseña para continuar.");
        return;
      }
      
      const { token, user } = response.data;
      loginWithToken(token, user);
      toast.success("¡Bienvenido de vuelta!");
      navigate(from, { replace: true });
    } catch (error) {
      console.error("Error logging in:", error);
      const detail = error.response?.data?.detail || "Error al iniciar sesión";
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  };

  const handleSetupPassword = async (e) => {
    e.preventDefault();
    if (!password || password.length < 8) {
      toast.error("La contraseña debe tener al menos 8 caracteres");
      return;
    }
    if (password !== confirmPassword) {
      toast.error("Las contraseñas no coinciden");
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/setup-password`, {
        email: email.toLowerCase().trim(),
        password,
        confirm_password: confirmPassword
      });
      
      const { token, user } = response.data;
      loginWithToken(token, user);
      toast.success("¡Contraseña creada! Bienvenido de vuelta.");
      navigate(from, { replace: true });
    } catch (error) {
      console.error("Error setting up password:", error);
      const detail = error.response?.data?.detail || "Error al crear la contraseña";
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  };

  // Admin authentication flow
  const handleAdminLogin = async (e) => {
    e.preventDefault();
    if (!adminEmail.trim() || !adminPassword) {
      toast.error("Por favor completa email y contraseña");
      return;
    }

    setAdminLoading(true);
    try {
      // First check if email is admin
      const checkResponse = await axios.get(`${API}/admin/check?email=${encodeURIComponent(adminEmail.toLowerCase().trim())}`);
      if (!checkResponse.data.is_admin) {
        toast.error("Este email no tiene acceso de administrador");
        setAdminLoading(false);
        return;
      }

      // Then login with credentials
      const loginResponse = await axios.post(`${API}/auth/login`, {
        email: adminEmail.toLowerCase().trim(),
        password: adminPassword
      });

      // Store token temporarily
      localStorage.setItem("authToken", loginResponse.data.token);
      
      // Move to PIN step
      setAdminStep("pin");
      toast.success("Credenciales verificadas. Ingresa tu PIN de seguridad.");
    } catch (error) {
      console.error("Admin login error:", error);
      if (error.response?.status === 401) {
        toast.error("Email o contraseña incorrectos");
      } else {
        toast.error(error.response?.data?.detail || "Error de autenticación");
      }
    } finally {
      setAdminLoading(false);
    }
  };

  const handleAdminPinVerify = async (e) => {
    e.preventDefault();
    if (!adminPin || adminPin.length < 4) {
      toast.error("Ingresa un PIN válido");
      return;
    }

    setAdminLoading(true);
    try {
      const response = await axios.post(`${API}/admin/verify-pin?email=${encodeURIComponent(adminEmail.toLowerCase().trim())}&pin=${adminPin}`);
      
      if (response.data.verified) {
        // Save PIN verification in session
        sessionStorage.setItem("adminPinVerified", adminEmail.toLowerCase().trim());
        
        // Get user data for context
        const token = localStorage.getItem("authToken");
        const userResponse = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        loginWithToken(token, userResponse.data);
        toast.success("Acceso de administrador verificado");
        setShowAdminModal(false);
        navigate("/admin");
      } else {
        toast.error("PIN incorrecto");
      }
    } catch (error) {
      console.error("PIN verify error:", error);
      toast.error("PIN incorrecto o error de verificación");
    } finally {
      setAdminLoading(false);
    }
  };

  const resetAdminModal = () => {
    setShowAdminModal(false);
    setAdminEmail("");
    setAdminPassword("");
    setAdminPin("");
    setAdminStep("credentials");
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background to-muted p-4">
      <div className="w-full max-w-md">
        {/* Back to home */}
        <Link to="/" className="flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6 transition-colors">
          <ArrowLeft className="h-4 w-4" />
          <span>Volver al inicio</span>
        </Link>
        
        <Card className="border-0 shadow-xl">
          <CardHeader className="text-center pb-2">
            <div className="mx-auto w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mb-4">
              <Mail className="h-6 w-6 text-primary" />
            </div>
            <CardTitle className="text-2xl">
              {step === "email" && "Iniciar sesión"}
              {step === "password" && "Bienvenido"}
              {step === "setup" && "Crea tu contraseña"}
            </CardTitle>
            <CardDescription>
              {step === "email" && "Ingresa tu email para continuar"}
              {step === "password" && "Ingresa tu contraseña para acceder"}
              {step === "setup" && "Esta es tu primera vez con contraseña. Tu historial está seguro."}
            </CardDescription>
          </CardHeader>
          
          <CardContent className="pt-4">
            {step === "email" && (
              <form onSubmit={handleCheckEmail} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="tu@email.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-10"
                      autoFocus
                      required
                    />
                  </div>
                </div>
                
                <Button type="submit" className="w-full" disabled={loading}>
                  {loading ? (
                    <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Verificando...</>
                  ) : (
                    "Continuar"
                  )}
                </Button>
              </form>
            )}
            
            {step === "password" && (
              <form onSubmit={handleLogin} className="space-y-4">
                <div className="p-3 bg-muted/50 rounded-lg text-sm text-center">
                  {email}
                  <button
                    type="button"
                    onClick={() => { setStep("email"); setPassword(""); }}
                    className="ml-2 text-primary hover:underline"
                  >
                    Cambiar
                  </button>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="password">Contraseña</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="pl-10 pr-10"
                      autoFocus
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                
                <div className="text-right">
                  <Link to="/forgot-password" state={{ email }} className="text-sm text-primary hover:underline">
                    ¿Olvidaste tu contraseña?
                  </Link>
                </div>
                
                <Button type="submit" className="w-full" disabled={loading}>
                  {loading ? (
                    <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Iniciando sesión...</>
                  ) : (
                    "Iniciar sesión"
                  )}
                </Button>
              </form>
            )}
            
            {step === "setup" && (
              <form onSubmit={handleSetupPassword} className="space-y-4">
                <div className="p-3 bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800 rounded-lg text-sm">
                  <p className="text-emerald-700 dark:text-emerald-300">
                    ✓ Tu cuenta <strong>{email}</strong> existe y todo tu historial está seguro.
                  </p>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="new-password">Nueva contraseña</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="new-password"
                      type={showPassword ? "text" : "password"}
                      placeholder="Mínimo 8 caracteres"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="pl-10 pr-10"
                      autoFocus
                      required
                      minLength={8}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="confirm-password">Confirmar contraseña</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="confirm-password"
                      type={showPassword ? "text" : "password"}
                      placeholder="Repite tu contraseña"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="pl-10"
                      required
                      minLength={8}
                    />
                  </div>
                </div>
                
                <Button type="submit" className="w-full" disabled={loading}>
                  {loading ? (
                    <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Creando contraseña...</>
                  ) : (
                    "Crear contraseña y entrar"
                  )}
                </Button>
              </form>
            )}
            
            <div className="mt-6 text-center text-sm text-muted-foreground">
              ¿No tienes cuenta?{" "}
              <Link to="/register" className="text-primary hover:underline font-medium">
                Regístrate
              </Link>
            </div>
          </CardContent>
        </Card>

        {/* Admin Access Button - Subtle */}
        <div className="mt-4 text-center">
          <button
            onClick={() => setShowAdminModal(true)}
            className="inline-flex items-center gap-1.5 text-xs text-muted-foreground/50 hover:text-muted-foreground transition-colors"
          >
            <Settings className="h-3 w-3" />
            Configuración
          </button>
        </div>
      </div>

      {/* Admin Access Modal */}
      <Dialog open={showAdminModal} onOpenChange={resetAdminModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5 text-primary" />
              Acceso Administrativo
            </DialogTitle>
            <DialogDescription>
              {adminStep === "credentials" 
                ? "Ingresa tus credenciales de administrador" 
                : "Ingresa tu PIN de seguridad"}
            </DialogDescription>
          </DialogHeader>

          {adminStep === "credentials" ? (
            <form onSubmit={handleAdminLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="admin-email">Email de administrador</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="admin-email"
                    type="email"
                    placeholder="admin@empresa.com"
                    value={adminEmail}
                    onChange={(e) => setAdminEmail(e.target.value)}
                    className="pl-10"
                    autoFocus
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="admin-password">Contraseña</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="admin-password"
                    type="password"
                    placeholder="••••••••"
                    value={adminPassword}
                    onChange={(e) => setAdminPassword(e.target.value)}
                    className="pl-10"
                    required
                  />
                </div>
              </div>

              <div className="flex gap-2 pt-2">
                <Button type="button" variant="outline" onClick={resetAdminModal} className="flex-1">
                  Cancelar
                </Button>
                <Button type="submit" className="flex-1" disabled={adminLoading}>
                  {adminLoading ? (
                    <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Verificando...</>
                  ) : (
                    "Continuar"
                  )}
                </Button>
              </div>
            </form>
          ) : (
            <form onSubmit={handleAdminPinVerify} className="space-y-4">
              <div className="p-3 bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800 rounded-lg text-sm text-center">
                <p className="text-emerald-700 dark:text-emerald-300">
                  ✓ Credenciales verificadas para <strong>{adminEmail}</strong>
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="admin-pin">PIN de Seguridad</Label>
                <div className="relative">
                  <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="admin-pin"
                    type="password"
                    placeholder="••••"
                    value={adminPin}
                    onChange={(e) => setAdminPin(e.target.value)}
                    className="pl-10 text-center text-lg tracking-widest"
                    maxLength={6}
                    autoFocus
                    required
                  />
                </div>
              </div>

              <div className="flex gap-2 pt-2">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setAdminStep("credentials")} 
                  className="flex-1"
                >
                  Atrás
                </Button>
                <Button type="submit" className="flex-1" disabled={adminLoading}>
                  {adminLoading ? (
                    <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Verificando...</>
                  ) : (
                    "Acceder"
                  )}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
